import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { LuxonDateAdapter, MAT_LUXON_DATE_FORMATS, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { JwtModule } from '@auth0/angular-jwt';

import {
  GoogleLoginProvider,
  GoogleSigninButtonDirective,
  SOCIAL_AUTH_CONFIG,
  SocialAuthServiceConfig,
  SocialLoginModule,
} from '@abacritt/angularx-social-login';
import { FullscreenOverlayContainer, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeArAE from '@angular/common/locales/ar-AE';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEnZA from '@angular/common/locales/en-ZA';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MaterialModule } from '@modules/material/material.module';
import { SecureMessageClaimComponent } from '@modules/secure-message/secure-message-claim/secure-message-claim.component';
import { SessionModule } from '@modules/session/session.module';
import { SharedModule } from '@modules/shared/shared.module';
import { TranslateFakeLoader, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { BlockEmailChangeComponent } from '@pages/block-email-change/block-email-change.component';
import { ChangeEmailComponent } from '@pages/change-email/change-email.component';
import { RegisterFirstStepComponent } from '@pages/register/register-first-step/register-first-step.component';
import { RegisterSecondStepComponent } from '@pages/register/register-second-step/register-second-step.component';
import { Config, ConfigLocaleService } from '@services/configLocale.service';
import { SimpleWebRTCService } from '@services/simple-web-rtc.service';
import { WebRTCService } from '@services/webrtc.service';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { CountdownModule } from 'ngx-countdown';
import { lastValueFrom } from 'rxjs';
import { UnauthorizedInterceptor } from 'src/app/interceptors/unauthorized.interceptor';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorReponseInterceptor } from './interceptors/error-response.interceptor';
import { ErrorRetryInterceptor } from './interceptors/error-retry.interceptor';
import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { AcceptInviteComponent } from './pages/accept-invite/accept-invite.component';
import { CompleteDetailsComponent } from './pages/complete-details/complete-details.component';
import { CompleteResetPasswordComponent } from './pages/complete-reset-password/complete-reset-password.component';
import { LandingComponent } from './pages/landing/landing.component';
import { LoginComponent } from './pages/login/login.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { RecoverAccountComponent } from './pages/recover-account/recover-account.component';
import { RegisterComponent } from './pages/register/register.component';
import { VerifyEmailComponent } from './pages/verify-email/verify-email.component';
import { WorkflowPdfComponent } from './pages/workflow-pdf/workflow-pdf.component';
import { TelNumberPipe } from './pipes/tel-number.pipe';

// Languages for dates and numbers
registerLocaleData(localeEnZA);
registerLocaleData(localeEnGB);
registerLocaleData(localeArAE);

// This function is used in the JwtModule.forRoot() call below
// It returns the access token from local storage or session storage
// Subsequent calls to the module's tokenGetter function or getRefreshToken()
// method will return the same value
export function tokenGetter() {
  return localStorage.getItem(environment.accessTokenKey) || sessionStorage.getItem(environment.accessTokenKey);
}

// This function is used to load the configuration before the app initializes
export function initializeApp(configService: ConfigLocaleService): () => Promise<void | Config[]> {
  return async () => {
    try {
      return await lastValueFrom(configService.loadConfig$());
    } catch (error) {
      console.error('Failed to load configuration:', error);
    }
  };
}

@NgModule({
  declarations: [
    AcceptInviteComponent,
    AppComponent,
    LoginComponent,
    RegisterComponent,
    RegisterFirstStepComponent,
    RegisterSecondStepComponent,
    CompleteDetailsComponent,
    CompleteResetPasswordComponent,
    VerifyEmailComponent,
    LandingComponent,
    TelNumberPipe,
    NotFoundComponent,
    RecoverAccountComponent,
    BlockEmailChangeComponent,
    ChangeEmailComponent,
    PrivacyPolicyComponent,
    WorkflowPdfComponent,
    SecureMessageClaimComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
    }),
    BrowserAnimationsModule,
    MaterialModule,
    SharedModule,
    JwtModule.forRoot({
      config: {
        tokenGetter,
        allowedDomains: environment.whitelistedDomains,
        disallowedRoutes: ['/api/auth/register', '/api/auth/login'],
      },
    }),
    GoogleTagManagerModule.forRoot({
      id: 'GTM-NF3RBLC',
      // gtm_resource_path: YOUR_GTM_RESOURCE_PATH
    }),
    OverlayModule,
    HammerModule,
    SessionModule,
    CountdownModule,
    PdfViewerModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en-ZA',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateFakeLoader,
      },
    }),
    SocialLoginModule,
    MatLuxonDateModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_LUXON_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translate: TranslateService) => translate.currentLang || 'en-ZA',
      deps: [TranslateService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorReponseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorRetryInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ConfigLocaleService],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
    { provide: LOCALE_ID, useValue: 'en-ZA' },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    { provide: WebRTCService, useClass: SimpleWebRTCService },
    TranslateService,
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider('727367228075-a6u5kim7hkai9s280p5fj6ieak3lind2.apps.googleusercontent.com', {
              prompt: 'select_account',
              oneTapEnabled: false,
              scopes: 'openid profile email',
              plugin_name: 'redirect',
            }),
          },
        ],
      } as SocialAuthServiceConfig,
    },
    GoogleSigninButtonDirective,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
